import { Check, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, MaskLine, SectionLabel } from "@/components/Reveal";

export default function ServiceDetail({
  headlineTop, headlineAccent, tagline, description,
  steps, benefits, benefitsHeading,
  submitLabel, pageTestId, ctaTitle, ctaSub,
}) {
  return (
    <>
      <section className="pt-[128px] pb-12 lg:pt-[156px] lg:pb-14" data-testid={pageTestId}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-display font-extrabold tracking-tight text-slate-900 text-4xl sm:text-6xl lg:text-7xl leading-[1.04] max-w-5xl">
            <MaskLine delay={0.18}>{headlineTop}</MaskLine>
            <MaskLine delay={0.3}><span className="text-brand-red">{headlineAccent}</span></MaskLine>
          </h1>
          <MaskLine delay={0.44} className="mt-8 max-w-2xl">
            <p className="text-base sm:text-lg text-brand-red font-medium" data-testid={`${pageTestId}-tagline`}>{tagline}</p>
          </MaskLine>
        </div>
      </section>

      <section className="pb-14 lg:pb-20" data-testid={`${pageTestId}-overview`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-3">
            <Reveal><SectionLabel>Overview</SectionLabel></Reveal>
          </div>
          <div className="lg:col-span-9 max-w-3xl space-y-6">
            {description.map((p, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <p className="text-base sm:text-lg text-slate-600 leading-relaxed">{p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-20 bg-brand-light/60" data-testid={`${pageTestId}-how`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionLabel>Process</SectionLabel>
            <h2 className="font-display font-bold tracking-tight text-slate-900 text-2xl sm:text-4xl mb-14">How It Works</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
            {steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <div data-testid={`${pageTestId}-step-${i + 1}`}>
                  <div className="flex items-center gap-4">
                    <span className="font-display font-extrabold text-4xl text-brand-red">{String(i + 1).padStart(2, "0")}</span>
                    {i < steps.length - 1 && <span className="hidden lg:block h-px flex-1 bg-gradient-to-r from-brand-red/50 to-slate-200" />}
                  </div>
                  <h3 className="mt-5 font-display font-semibold text-slate-900 text-lg">{s.title}</h3>
                  <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-20" data-testid={`${pageTestId}-benefits`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionLabel>Benefits</SectionLabel>
            <h2 className="font-display font-bold tracking-tight text-slate-900 text-2xl sm:text-4xl mb-12">{benefitsHeading}</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-x-14 gap-y-6 max-w-4xl">
            {benefits.map((b, i) => (
              <Reveal key={b} delay={i * 0.05}>
                <div className="flex items-start gap-4" data-testid={`${pageTestId}-benefit-${i + 1}`}>
                  <span className="mt-0.5 inline-flex p-1.5 rounded-full bg-brand-light text-brand-red shrink-0">
                    <Check size={14} strokeWidth={3} />
                  </span>
                  <p className="text-sm sm:text-base text-slate-700 leading-relaxed">{b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-brand-red" data-testid={`${pageTestId}-cta`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-display font-bold tracking-tight text-white text-2xl sm:text-4xl text-center">{ctaTitle}</h2>
            <p className="mt-4 text-white/85 text-center text-base sm:text-lg max-w-xl mx-auto">{ctaSub}</p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-10 bg-white p-7 sm:p-10 text-center">
              <p className="text-sm text-slate-500 max-w-md mx-auto">
                A guided conversation about your requirement takes a few minutes — and gives our team everything needed to respond with the right approach.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row justify-center gap-4">
                <Link to="/discuss-your-requirement" data-testid={`${pageTestId}-discuss-cta`}
                  className="group inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-dark text-white font-medium text-sm px-8 py-4 transition-all duration-300 hover:-translate-y-0.5">
                  {submitLabel}
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link to="/how-genspire-works" data-testid={`${pageTestId}-how-cta`}
                  className="group inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-brand-red text-slate-800 hover:text-brand-red font-medium text-sm px-8 py-4 transition-all duration-300">
                  How Genspire Works
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
